import React, { useMemo, useCallback, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { Box } from "@material-ui/core";
import { asCurrency } from "../../../../../utils";
import CustomCardHeader from "../../CustomCardHeader";
import { Grid } from "@material-ui/core";
import Button from "components/CustomButtons/Button";
import { Close, Edit } from "@material-ui/icons";
import ChapterBoardMemberDialog from "./ChapterBoardMemberDialog";

import {
  REPORT_ITEM_TYPE,
  REPORT_ITEM_SUB_TYPE
} from "../../../../../constants";

const useStyles = makeStyles({
  tabSubTitle: {
    background: "#aaa",
    textTransform: "capitalize"
  },
  cursorPointer: {
    cursor: "pointer"
  },
  position: {
    fontSize: "0.75rem"
  },
  flexGrow: {
    flexGrow: 1
  },
  actions: {
    margin: "0 0 0 5px",
    padding: 5,
    "& svg": {
      margin: 0
    }
  }
});

const ChapterBoardMember = ({
  items,
  reportID,
  data,
  chapterID,
  year,
  onHelp,
  isEditable,
  createReportItem,
  updateReportItem,
  initialReportItems,
  removeReportItem,
  onRefetch,
  onRedirect
}) => {
  const classes = useStyles();

  const [activeData, setActiveData] = useState({
    item: {},
    isAdmin: false,
    isCreating: true
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const officers = useMemo(() => {
    return items.filter(
      item => item.reportItemSubTypeID === REPORT_ITEM_SUB_TYPE.OFFICER
    );
  }, [items]);

  const onCreate = useCallback(
    async (item, isAdmin) => {
      setDialogOpen(false);

      const { hoursPerWeek, amount, ...itemData } = item;

      let body = {
        hoursPerWeek: +hoursPerWeek,
        amount: +amount,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.OFFICER,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE,
        officer: { ...itemData, chapterID }
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await createReportItem({ ...body, reportID });
      }

      onRefetch();
    },
    [reportID, activeData, chapterID]
  );

  const onUpdate = useCallback(
    async (item, isAdmin) => {
      setDialogOpen(false);

      const { hoursPerWeek, amount, ...itemData } = item;

      let body = {
        hoursPerWeek: +hoursPerWeek,
        amount: +amount,
        reportItemSubTypeID: REPORT_ITEM_SUB_TYPE.OFFICER,
        reportItemTypeID: REPORT_ITEM_TYPE.EXPENSE,
        officer: itemData
      };

      if (reportID === "add") {
        const _reportID = await initialReportItems({
          chapterID,
          year,
          items: [body]
        });
        onRedirect(_reportID);
      } else {
        await updateReportItem(activeData.item.id, { ...body, reportID });
      }

      onRefetch();
    },
    [reportID, activeData]
  );

  const openCreateDialog = useCallback(isAdmin => {
    setActiveData({
      item: {},
      isAdmin,
      isCreating: true
    });
    setDialogOpen(true);
  }, []);

  const openUpdateDialog = useCallback((item, isAdmin) => {
    setActiveData({
      item,
      isAdmin,
      isCreating: false
    });
    setDialogOpen(true);
  }, []);

  const onRemove = useCallback(async item => {
    await removeReportItem(item.id);
    onRefetch();
  }, []);

  if (officers.length > 0) {
    return (
      <Box mb={3}>
        <CustomCardHeader
          label={"Chapter Board Member Data"}
          onHelp={() =>
            onHelp(REPORT_ITEM_TYPE.EXPENSE, REPORT_ITEM_SUB_TYPE.OFFICER)
          }
        />
        {officers.map(item => (
          <Box px={1} py={0.5} key={`officer-${item.id}`}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={8}>
                <Typography className={classes.name}>
                  <Typography>
                    {item.officer.firstName} {item.officer.lastName}
                  </Typography>
                  <Typography className={classes.position}>
                    {item.officer.position}
                  </Typography>
                  <Typography className={classes.position}>
                    {item.officer.title}
                  </Typography>
                  <Typography className={classes.position}>
                    Hours per week: {item.hoursPerWeek}
                  </Typography>
                  <Typography className={classes.position}>
                    Voting Officer: {item.officer.votingOfficer ? "Yes" : "No"}
                  </Typography>
                </Typography>
              </Grid>
              <Grid item className={classes.flexGrow}>
                <Typography align="right">
                  {asCurrency(item.amount || 0)}
                </Typography>
              </Grid>
              {isEditable && (
                <Grid item>
                  <Box display="flex">
                    <Button
                      simple
                      color="success"
                      className={classes.actions}
                      onClick={() => openUpdateDialog(item, false)}
                    >
                      <Edit />
                    </Button>
                    <Button
                      simple
                      color="danger"
                      onClick={() => onRemove(item)}
                      className={classes.actions}
                    >
                      <Close />
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        ))}
        {isEditable && (
          <Box ml={3} mt={2}>
            <a onClick={() => openCreateDialog(false)}>
              <strong>Add a Board Member</strong>
            </a>
          </Box>
        )}

        <ChapterBoardMemberDialog
          open={dialogOpen}
          data={activeData}
          onClose={() => setDialogOpen(false)}
          onCreate={onCreate}
          onUpdate={onUpdate}
        />
      </Box>
    );
  } else {
    return <div></div>;
  }
};

export default ChapterBoardMember;
